import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, forkJoin, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { ClosableModalWithErrorResponseModel } from '../../../shared/components/base/closable-modal-with-error-response-model';
import { DropdownsModalComponent } from '../../../shared/components/dropdowns-modal/dropdowns-modal.component';

import { NotificationService, FunctionalAnalysesService, PbasService } from '../../../shared/services';
import { ModalService } from '../../../shared/modules/modal';

import { ObjectResponse, ListResponse } from '../../../shared/interfaces';
import {
  ModifyFunctionalAnalysisTemplateModalPayload, DropdownsModalPayload, XbsEnum, MajorServiceClassification, DropdownsModalDropdownData,
  PbaFunctionalAnalysisGlobalTemplate, ImportFunctionalAnalysisGlobalTemplatePayload
} from '../../../shared/models';
import { EnumsWithTranslationsConstants } from '../../../shared/constants';
import { ConfigurationConstants } from '../../../shared/constants/config';

@Component({
  template: ''
})
export class ModifyPbaFunctionalAnalysisTemplateModalComponent extends ClosableModalWithErrorResponseModel<void> implements OnInit, OnDestroy {

  data: ModifyFunctionalAnalysisTemplateModalPayload;

  private testedPartyCharacterizations: XbsEnum[];
  private distributorTypes: XbsEnum[];
  private allMajorClassifications: MajorServiceClassification[];
  private allGlobalTemplates: PbaFunctionalAnalysisGlobalTemplate[];
  private selectedGlobalTemplateId: number;

  private testedPartyCharDropdown: DropdownsModalDropdownData;
  private filterDropdown: DropdownsModalDropdownData = {} as DropdownsModalDropdownData;
  private distributorTypeDropdown: DropdownsModalDropdownData;
  private majorClassificationDropdown: DropdownsModalDropdownData;
  private globalTemplateDropdown: DropdownsModalDropdownData;
  private wasTemplateModifySuccessful$: Subject<boolean> = new Subject<boolean>();

  constructor(
    protected notificationService: NotificationService,
    protected modalService: ModalService,
    private _functionalAnalysesService: FunctionalAnalysesService,
    private _translateService: TranslateService,
    private _pbasService: PbasService
  ) {
    super(notificationService, modalService);
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.getAllModalData().subscribe(() => {
        this.initializeModalDropdowns();
        (this.data.isDeleteModal) ? this.openGenericDeleteModal() : this.openGenericImportModal();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private getAllModalData(): Observable<[ObjectResponse<MajorServiceClassification[]>, ListResponse<PbaFunctionalAnalysisGlobalTemplate>]> {
    this.getTestedPartyCharacterizations();
    this.getDistributorTypes();

    const asyncModalData$: Observable<[ObjectResponse<MajorServiceClassification[]>, ListResponse<PbaFunctionalAnalysisGlobalTemplate>]> = forkJoin(
      this.getAllMajorClassifications(),
      this.getAllGlobalTemplates()
    );

    return asyncModalData$;
  }

  private getTestedPartyCharacterizations(): void {
    const primaryFunctionsEnums: XbsEnum[] = Object.values(EnumsWithTranslationsConstants.PRIMARY_FUNCTIONS);

    for (const primaryFuncEnum of primaryFunctionsEnums) {
      primaryFuncEnum.Value = this._translateService.instant(primaryFuncEnum.Value);
    }

    this.testedPartyCharacterizations = primaryFunctionsEnums;
  }

  private getDistributorTypes(): void {
    const functionsPerformedEnums: XbsEnum[] = Object.values(EnumsWithTranslationsConstants.FUNCTIONS_PERFORMED);

    for (const funcPerformedEnum of functionsPerformedEnums) {
      funcPerformedEnum.Value = this._translateService.instant(funcPerformedEnum.Value);
    }

    this.distributorTypes = functionsPerformedEnums;
  }

  private getAllMajorClassifications(): Observable<ObjectResponse<MajorServiceClassification[]>> {
    return this._pbasService.listAllMajorServiceClassifications().pipe(
      tap(({ data: allMajors }: ObjectResponse<MajorServiceClassification[]>) => {
        for (const major of allMajors) {
          major.name = this._translateService.instant(major.name);
        }

        this.allMajorClassifications = allMajors;
        this.allMajorClassifications.sort((major1, major2) => {
          return major1.name > major2.name ? 1 : -1;
        });
      })
    );
  }

  private getAllGlobalTemplates(): Observable<ListResponse<PbaFunctionalAnalysisGlobalTemplate>> {
    return this._functionalAnalysesService.getAllGlobalTemplates(this.data.analysisType).pipe(
      tap(({ data: globalTemplates }: ListResponse<PbaFunctionalAnalysisGlobalTemplate>) => {
        this.allGlobalTemplates = globalTemplates;
      })
    );
  }

  private initializeModalDropdowns(): void {
    this.testedPartyCharDropdown = {
      name: 'pba.tested_party_characterization',
      elements: this.testedPartyCharacterizations,
      keyForDisplay: 'Value',
      onSelected: this.onTestedPartyCharChange.bind(this)
    };

    this.distributorTypeDropdown = {
      name: 'comp_search.distributor_type',
      elements: this.distributorTypes,
      keyForDisplay: 'Value',
      onSelected: this.onDistributorTypeChange.bind(this),
      multiSelectMaxNumber: 0
    };

    this.majorClassificationDropdown = {
      name: 'major_service_classification.major_classification',
      elements: this.allMajorClassifications,
      keyForDisplay: 'name',
      onSelected: this.onMajorClassificationChange.bind(this),
      multiSelectMaxNumber: 3
    };

    this.filterDropdown = Object.assign(this.filterDropdown, this.distributorTypeDropdown);

    this.globalTemplateDropdown = {
      name: 'functional_analysis.template',
      elements: this.allGlobalTemplates,
      keyForDisplay: 'templateName',
      onSelected: this.onGlobalTemplateSelect.bind(this)
    };
  }

  private onTestedPartyCharChange(characterization: XbsEnum): void {
    const characterizationId = characterization.Id;

    const isDistributorChosen = (characterizationId === EnumsWithTranslationsConstants.PRIMARY_FUNCTIONS.DISTRIBUTOR.Id);
    const dropdownToSwitchTo = isDistributorChosen ? this.distributorTypeDropdown : this.majorClassificationDropdown;

    Object.assign(this.filterDropdown, dropdownToSwitchTo);
  }

  private onDistributorTypeChange(distributorEnum: XbsEnum): void {
    const { Id: hasValueAdded } = distributorEnum;

    this.globalTemplateDropdown.elements = this.allGlobalTemplates.filter((template) => {
      return template.hasValueAdded === !!hasValueAdded && template.primaryFunctionId !== EnumsWithTranslationsConstants.PRIMARY_FUNCTIONS.SERVICE_PROVIDER.Id;
    });
  }

  private onMajorClassificationChange(chosenMajors: MajorServiceClassification[]): void {
    this.globalTemplateDropdown.elements = this.allGlobalTemplates.filter((template) => {
      let doesMatchMajorIds: boolean = false;

      if (template.primaryFunctionId !== EnumsWithTranslationsConstants.PRIMARY_FUNCTIONS.DISTRIBUTOR.Id) {
        doesMatchMajorIds = chosenMajors.every(major => template.majorClassificationIds.includes(major.majorServiceClassificationId));
      }

      return doesMatchMajorIds;
    });
  }

  private onGlobalTemplateSelect(globalTemplate: PbaFunctionalAnalysisGlobalTemplate): void {
    this.selectedGlobalTemplateId = globalTemplate.functionalAnalysisGlobalTemplateId;
  }

  private openGenericDeleteModal(): void {
    this.modalService.openModal({
      component: DropdownsModalComponent,
      data: {
        modalTitle: 'functional_analysis.delete_functional_analysis_template',
        confirmationText: 'functional_analysis.delete_confirmation_text',
        targetConfirmationText: ConfigurationConstants.CONFIRMATION_TEXT,
        dropdowns: [
          this.testedPartyCharDropdown,
          this.filterDropdown,
          this.globalTemplateDropdown
        ],
        saveClicked: this.deleteTemplate.bind(this),
        saved: this.wasTemplateModifySuccessful$
      } as DropdownsModalPayload,
      success: this.success,
      cancel: () => this.wasTemplateModifySuccessful$.next(false)
    });
  }

  private deleteTemplate(): void {
    this.subscriptions.push(
      this._functionalAnalysesService.deleteGlobalTemplate(this.data.analysisType, this.selectedGlobalTemplateId).subscribe({
        next: () => this.wasTemplateModifySuccessful$.next(true),
        error: () => this.wasTemplateModifySuccessful$.next(false)
      })
    );
  }

  private openGenericImportModal(): void {
    this.modalService.openModal({
      component: DropdownsModalComponent,
      data: {
        modalTitle: 'functional_analysis.import_functional_analysis_template',
        dropdowns: [
          this.testedPartyCharDropdown,
          this.filterDropdown,
          this.globalTemplateDropdown
        ],
        saveClicked: this.importTemplate.bind(this),
        saved: this.wasTemplateModifySuccessful$
      } as DropdownsModalPayload,
      success: this.success,
      cancel: () => this.wasTemplateModifySuccessful$.next(false)
    });
  }

  private importTemplate(): void {
    const payload: ImportFunctionalAnalysisGlobalTemplatePayload = { templateId: this.selectedGlobalTemplateId };

    this.subscriptions.push(
      this._functionalAnalysesService.importGlobalTemplate(this.data.analysisType, this.data.resourceId, payload).subscribe({
        next: () => this.wasTemplateModifySuccessful$.next(true),
        error: () => this.wasTemplateModifySuccessful$.next(false)
      })
    );
  }

}
